import {SliceZone} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {NavigationDocumentData, SubmenuDocumentData} from 'prismicio-types'
import {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useClickAway, useWindowSize} from 'react-use'
import {Icon} from 'src/common/Icon'
import {isFilled} from '@prismicio/client'
import LinksColumn from 'slices/LinksColumn'
import {NavElement} from './NavElement'
import {useLinkResolver} from 'prismicio'
import {ButtonV2} from 'src/common/ButtonV2'
import {useDynamicBackground} from 'src/common/hooks'
import {DesktopSubmenu} from './components/DesktopSubmenu'
import {MorphicDropdownToggleSVG} from './components/MorphicDropdownToggleSVG'
import {BASE_URL, BREAKPOINTS} from 'src/common/constants'
import {EVENTS, track} from 'src/common/helpers'
import {DialogContext} from 'src/context/DialogContext'

export const HeaderV3 = memo(function HeaderV3(props: {
  className?: string
  navigationData: NavigationDocumentData
  isDarkNavigation?: boolean
  pageUid?: string
}) {
  const {linkResolver} = useLinkResolver()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const toggleMenu = useCallback(() => setIsMenuOpen((prevState) => !prevState), [])
  const headerRef = useRef(null)
  const {isDialogOpen} = useContext(DialogContext) || {isDialogOpen: false}

  useClickAway(headerRef, () => {
    setIsMenuOpen(false)
  })

  // Close mobile menu when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      setIsMenuOpen(false)
      setIsSubmenuOpen(false)
      setActiveIndex(null)
    }
  }, [isDialogOpen])

  const isMobile = useWindowSize().width < BREAKPOINTS.DESKTOP
  const {isBackgroundDark} = useDynamicBackground({initialState: true})
  const navRef = useRef<HTMLDivElement>(null)

  // Get submenu width on mobile
  useEffect(() => {
    const target = document.getElementById('nav-bar-container')
    const nav = navRef.current

    if (target && nav) {
      const updateWidth = () => {
        if ((window?.visualViewport?.width || 0) < BREAKPOINTS.DESKTOP) {
          nav.style.width = `${target.offsetWidth}px`
        } else {
          nav.style.removeProperty('width')
        }
      }

      updateWidth()
      window.addEventListener('resize', updateWidth)

      return () => window.removeEventListener('resize', updateWidth)
    }

    return () => {}
  }, [])

  const handleMouseEnter = useCallback(
    (index: number | null) => {
      setActiveIndex(index)

      if (!isMobile) {
        setIsSubmenuOpen(true)
      }
    },
    [isMobile],
  )

  const handleClick = (index: number) => {
    if (activeIndex === index) {
      setIsSubmenuOpen((prev) => !prev)
      setActiveIndex(null)
    } else {
      setActiveIndex(index)
      setIsSubmenuOpen(true)
    }
  }

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsSubmenuOpen(false)
    }

    setActiveIndex(null)
  }, [isMobile])

  const submenus = useMemo(
    () =>
      props.navigationData.menu_links.map((section, index) => {
        if (isFilled.contentRelationship<string, string, SubmenuDocumentData>(section.submenu)) {
          const linksColumns = section.submenu.data?.slices.map((slice) => slice.variation === 'default')
          const shouldHideTitle = Number(linksColumns?.length) <= 1

          return (
            <SliceZone
              key={index}
              components={{links_column: LinksColumn}}
              slices={section.submenu.data?.slices}
              context={{shouldHideTitle, pageUid: props.pageUid}}
            />
          )
        } else {
          return []
        }
      }),
    [props.navigationData.menu_links, props.pageUid],
  )

  return (
    <>
      <div
        className={cn('sticky top-0 z-50 w-full px-5 pt-3 transition-all duration-300 md:max-lg:px-10 xl:px-0', {
          'pointer-events-none': isDialogOpen,
        })}
        ref={headerRef}
      >
        <header
          className={cn(
            'relative left-1/2 w-full max-w-[1200px] -translate-x-1/2 rounded-[4px] backdrop-blur-[15px] lg:py-2 lg:pr-2',
            {
              'bg-moss-white/[20%] text-moss-white': isBackgroundDark,
              'bg-moss-white/[50%] max-lg:text-black': !isBackgroundDark,
            },
          )}
          id="header-container"
        >
          <div
            className={cn('pointer-events-none absolute inset-0 rounded-[4px] border bg-transparent', {
              'border-moss-white/[10%]': isBackgroundDark,
              'border-black/[10%]': !isBackgroundDark,
            })}
          />
          <div
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-[4px] py-[7px] pl-3 pr-[7px] lg:rounded-none lg:bg-transparent lg:p-0',
            )}
            id="nav-bar-container"
          >
            <Link
              className="flex pl-0 lg:pl-4"
              href={linkResolver({link_type: 'Web', url: BASE_URL})}
              onClick={() =>
                track({
                  event: EVENTS.CLICK,
                  location: 'nav-bar',
                  dataType: 'logo-link',
                  mktgId: 'logo',
                  pageName: props.pageUid,
                })
              }
            >
              <Icon className="h-[30px] w-[103px] lg:h-[32px] lg:w-[98px]" name="moss-logo-v2" />
            </Link>

            <div className="flex items-center gap-2">
              <ButtonV2
                className="text-button-xs !h-8 !px-3 !py-2 uppercase max-lg:hidden"
                href={linkResolver(props.navigationData.secondary_button_url)}
                variant="secondary"
                size="small"
                trackingData={{
                  location: 'nav-bar',
                  dataType: 'secondary-button',
                  mktgId: props.navigationData.secondary_button_mktg_id!,
                  pageName: props.pageUid,
                }}
                id="header-secondary-button"
              >
                {props.navigationData.secondary_button_label}
              </ButtonV2>

              <ButtonV2
                className="text-button-xs !h-8 !px-3 !py-2 uppercase"
                size="small"
                data-page-location="nav-bar"
                variant="primary"
                href={linkResolver(props.navigationData.primary_button_url, 'primary-button-header')}
                trackingData={{
                  location: 'nav-bar',
                  dataType: 'primary-button',
                  mktgId: props.navigationData.primary_button_mktg_id!,
                  pageName: props.pageUid,
                }}
                id="header-primary-button"
              >
                {props.navigationData.primary_button_label}
              </ButtonV2>

              <ButtonV2
                variant="icon-secondary"
                size="small"
                className="relative h-8 w-8 p-[11px] lg:hidden"
                trackingData={{
                  mktgId: 'mobile-menu-toggle',
                  location: 'nav-bar',
                  dataType: isMenuOpen ? 'close-button' : 'open-button',
                  pageName: props.pageUid,
                }}
                onClick={toggleMenu}
              >
                <MorphicDropdownToggleSVG isOpen={isMenuOpen} />
              </ButtonV2>
            </div>
            {/* Old position */}
          </div>
        </header>
        <nav
          className={cn(
            `absolute left-5 top-[68px] flex w-full flex-col gap-x-9 overscroll-contain rounded-[4px] max-lg:max-h-[85vh] max-lg:overflow-y-auto max-lg:border max-lg:border-black/[10%] max-lg:bg-moss-white/[95%] max-lg:backdrop-blur-[15px] md:max-lg:left-10 lg:left-1/2  lg:top-3 lg:h-[48px] lg:w-fit lg:-translate-x-1/2 lg:grid-flow-col lg:flex-row lg:items-center lg:justify-center lg:p-0`,
            {
              'max-lg:opacity-0 max-lg:invisible max-lg:pointer-events-none': !isMenuOpen,
              'max-lg:transition-all max-lg:duration-300': isMobile,
            },
            {
              'text-moss-white': isBackgroundDark,
              'max-lg:text-black': !isBackgroundDark,
            },
          )}
          ref={navRef}
        >
          {props.navigationData.menu_links.map((section, index) => {
            const hasSubmenu = isFilled.contentRelationship<string, string, SubmenuDocumentData>(section.submenu)
            const clickableProps = hasSubmenu ? {onClick: () => handleClick(index)} : {}
            const hoverProps = hasSubmenu && !isMobile ? {onMouseEnter: () => handleMouseEnter(index)} : {}

            return (
              <div className="h-full" key={'nav-element-' + index} {...clickableProps} {...hoverProps}>
                <NavElement
                  index={index}
                  section={section}
                  menuLinks={props.navigationData.menu_links.length}
                  pageUid={props.pageUid || ''}
                  activeIndex={activeIndex}
                  isDarkBackground={isBackgroundDark}
                  isMobile={isMobile}
                  isSubmenuOpen={isMenuOpen}
                  setIsMenuOpen={setIsMenuOpen}
                  activeSubmenu={submenus[index]}
                  handleMouseEnter={() => handleMouseEnter(index)}
                  handleMouseLeave={handleMouseLeave}
                  setActiveIndex={setActiveIndex}
                />
              </div>
            )
          })}

          <div className="w-full px-2 pb-2 pt-8 lg:hidden">
            <ButtonV2
              className="uppercase lg:hidden"
              href={linkResolver(props.navigationData.secondary_button_url)}
              variant="primary"
              size="small"
              trackingData={{
                location: 'nav-bar',
                dataType: 'secondary-button',
                mktgId: props.navigationData.secondary_button_mktg_id!,
                pageName: props.pageUid,
              }}
              id="header-secondary-button"
            >
              {props.navigationData.secondary_button_label}
            </ButtonV2>
          </div>
        </nav>
        <DesktopSubmenu
          isOpen={isSubmenuOpen}
          handleMouseEnter={() => handleMouseEnter(activeIndex)}
          handleMouseLeave={handleMouseLeave}
        >
          {submenus[activeIndex || 0]}
        </DesktopSubmenu>
      </div>
    </>
  )
})
